import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';

interface AsyncStateProps {
  loading: boolean;
  error: string | null;
  isEmpty?: boolean;
  onRetry?: () => void;
  loadingTitle?: string;
  emptyTitle?: string;
  emptyBody?: string;
  children: React.ReactNode;
}

export function AsyncState({
  loading,
  error,
  isEmpty = false,
  onRetry,
  loadingTitle,
  emptyTitle = 'No data',
  emptyBody,
  children,
}: AsyncStateProps) {
  const { theme } = useTheme();

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
        {loadingTitle && (
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>{loadingTitle}</Text>
        )}
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorTitle, { color: theme.colors.error }]}>Error</Text>
        <Text style={[styles.errorMessage, { color: theme.colors.textSecondary }]}>{error}</Text>
        {onRetry && (
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: theme.colors.accent }]}
            onPress={onRetry}
          >
            <Text style={[styles.retryButtonText, { color: theme.colors.accentForeground }]}>Retry</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  if (isEmpty) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>{emptyTitle}</Text>
        {emptyBody && (
          <Text style={[styles.emptyBody, { color: theme.colors.textSecondary }]}>{emptyBody}</Text>
        )}
      </View>
    );
  }

  return <>{children}</>;
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  loadingText: {
    fontSize: 14,
    marginTop: 12,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 2,
  },
  retryButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 14,
    textAlign: 'center',
  },
});
